import { DataGrid, IconButton, Label, Menu } from '@neo4j-ndl/react';
import { EllipsisHorizontalIconOutline } from '@neo4j-ndl/react/icons';
import { CLOUD_PROVIDER, DESTINATION_TYPE, INSTANCE_STATUS, useInstances } from '@nx/state';
import type { Destination, LogForwarding, LogForwardingIdentity } from '@nx/state';
import { DataGridHelpers, SearchField } from '@nx/ui';
import { CloudProviderIcon } from '@nx/ui/src/cloud-provider-icon/cloud-provider-icon';
import type { CellContext, Row } from '@tanstack/react-table';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useMemo, useRef, useState } from 'react';

import { ACTIONS_COLUMN_ID } from '../logs/shared/types';
import { CommonNoDataPlaceholder } from '../shared/helpers/table-overrides';
import { getProductFromTier, regionNameWithInstanceCount } from './helpers/utils';

interface Props {
  data: LogForwarding[];
  onView: (logForwarding: LogForwarding) => void;
  onEdit: (logForwarding: LogForwarding) => void;
  onDelete: (logForwarding: LogForwarding) => void;
  logForwardingIdentities: LogForwardingIdentity[];
}

const helper = createColumnHelper<LogForwarding>();

const LogForwardingActionCell = ({
  cell,
  onView,
  onEdit,
  onDelete,
}: {
  cell: CellContext<LogForwarding<Destination>, unknown>;
  onView: (logForwarding: LogForwarding) => void;
  onEdit: (logForwarding: LogForwarding) => void;
  onDelete: (logForwarding: LogForwarding) => void;
}) => {
  const anchorEl = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <span className="flex w-full justify-end gap-3">
      <IconButton
        className="flex w-8 flex-row-reverse"
        onClick={() => {
          setIsOpen(true);
        }}
        ref={anchorEl}
        ariaLabel={'Log forwarding actions'}
        isClean={true}
        data-testid="log-forwarding-ellipsis"
      >
        <EllipsisHorizontalIconOutline />
      </IconButton>
      <Menu isOpen={isOpen} onClose={() => setIsOpen(false)} anchorRef={anchorEl}>
        <Menu.Items>
          <Menu.Item onClick={() => onView(cell.row.original)} title="View configuration" />
          <Menu.Item
            htmlAttributes={{
              'data-testid': 'edit-log-forwarding-button',
            }}
            onClick={() => onEdit(cell.row.original)}
            title="Edit"
          />
          <Menu.Divider />
          <Menu.Item
            htmlAttributes={{
              'data-testid': 'delete-log-forwarding-button',
            }}
            onClick={() => onDelete(cell.row.original)}
            className="text-palette-danger-text"
            title="Delete"
          />
        </Menu.Items>
      </Menu>
    </span>
  );
};

export const LogForwardingTable = ({ data, onView, onEdit, onDelete }: Props) => {
  const { instances, isInstancesLoading } = useInstances({ shouldPoll: false });
  const [searchString, setSearchString] = useState<string>('');
  const filteredData = useMemo(() => {
    return data.filter(
      (lf) =>
        lf.name.startsWith(searchString) || lf.region.startsWith(searchString) || lf.logType.startsWith(searchString),
    );
  }, [data, searchString]);
  const hasDbIdTargets = useMemo(
    () => data.some((lf) => lf.db_id !== undefined && lf.db_id !== '*' && lf.db_id !== ''),
    [data],
  );
  const getCloudProvider = (destinationType: DESTINATION_TYPE) => {
    switch (destinationType) {
      default:
      case DESTINATION_TYPE.STACKDRIVER:
        return CLOUD_PROVIDER.GCP;
      case DESTINATION_TYPE.CLOUDWATCH:
        return CLOUD_PROVIDER.AWS;
      case DESTINATION_TYPE.LOG_ANALYTICS:
        return CLOUD_PROVIDER.AZURE;
    }
  };

  const columns = useMemo(() => {
    const getInstance = (a: Row<LogForwarding<Destination>>) => instances.find((i) => i.id === a.original.db_id);
    const getInstanceName = (a: Row<LogForwarding<Destination>>) => getInstance(a)?.name ?? 'unknown';

    const commonDefs = [
      helper.accessor('logType', {
        header: 'Logs',
        minSize: 100,
        cell: (cell) => <span className="capitalize">{cell.getValue()}</span>,
      }),
      helper.accessor('ready', {
        header: 'Status',
        minSize: 100,
        cell: (cell) => {
          if (getInstance(cell.row)?.instanceStatus === INSTANCE_STATUS.PAUSED) {
            return (
              <Label hasIcon fill="semi-filled">
                Paused
              </Label>
            );
          }
          return cell.getValue() ? (
            <Label hasIcon fill="semi-filled" color="success">
              Forwarding
            </Label>
          ) : (
            <Label hasIcon fill="semi-filled">
              Setting up
            </Label>
          );
        },
      }),
      helper.display({
        maxSize: 50,
        id: 'actions',
        cell: (cell) => <LogForwardingActionCell cell={cell} onView={onView} onEdit={onEdit} onDelete={onDelete} />,
      }),
    ];

    let colDefs;
    if (hasDbIdTargets) {
      colDefs = [
        helper.accessor('db_id', {
          header: 'Instance',
          minSize: 300,
          sortingFn: (a, b) => {
            const instanceA = getInstanceName(a);
            const instanceB = getInstanceName(b);
            return instanceA.localeCompare(instanceB);
          },
          cell: ({ row }) => {
            const displayName = getInstanceName(row);
            return (
              <div className="flex items-center gap-2">
                <CloudProviderIcon cloudProvider={getCloudProvider(row.original.destination.type)} />
                {displayName}
              </div>
            );
          },
        }),
        ...commonDefs,
      ];
    } else {
      colDefs = [
        helper.accessor('name', {
          header: 'Configuration',
          minSize: 100,
          cell: ({ row }) => row.original.name,
        }),
        helper.accessor('tier', {
          cell: (c) => {
            return getProductFromTier(c.row.original.tier);
          },
          header: 'Product',
        }),
        helper.accessor('region', {
          header: 'Region',
          minSize: 100,
          cell: (cell) => {
            const { region } = cell.row.original;
            const { tier } = cell.row.original;
            const dbs = instances.filter((db) => db.region === region && db.tier === tier);
            return regionNameWithInstanceCount(region, dbs);
          },
        }),
        ...commonDefs,
      ];
    }
    return colDefs;
  }, [hasDbIdTargets, onView, onEdit, onDelete, instances]);

  const table = useReactTable({
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    columns,
    data: filteredData,
    columnResizeMode: 'onChange',
    enableColumnPinning: true,
    state: {
      columnPinning: {
        right: [ACTIONS_COLUMN_ID],
      },
    },
  });

  return (
    <DataGridHelpers.Wrapper>
      <DataGridHelpers.OuterHeader>
        <div className="flex w-full flex-wrap items-center justify-between gap-x-12 gap-y-2">
          <div className="mr-auto flex basis-[400px] gap-2">
            <SearchField
              className="min-w-36 grow"
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              htmlAttributes={{ 'aria-label': 'Search for instances' }}
            />
          </div>
        </div>
      </DataGridHelpers.OuterHeader>
      <DataGrid
        rootProps={{
          className: `[&_.ndl-data-grid-pinned-cell-right]:grow`,
        }}
        styling={{ headerStyle: 'clean' }}
        isLoading={isInstancesLoading}
        isAutoResizingColumns={false}
        tableInstance={table}
        components={{
          NoDataPlaceholder: () => {
            return <CommonNoDataPlaceholder>No matching rows</CommonNoDataPlaceholder>;
          },
          RowsPerPage: DataGridHelpers.RowsPerPageWithBodyMenuPortal,
        }}
        isKeyboardNavigable={false}
      />
    </DataGridHelpers.Wrapper>
  );
};
